import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/new-components/ui/button';
import { SimpleAccordion } from '../components/new-components/SimpleAccordion';
import { JoinTheHiveForm } from '../components/new-components/JoinTheHiveForm';
import { PublicHeader } from '../components/Layout';
import { NavItem } from '../components/Layout/navConfig';
import { BRAND_ASSETS, TENANT } from '../config';
import { TRUST_INTRO, TRUST_SECTIONS, TRUST_FORWARD_STATEMENT, FORWARD_ICON } from '../config/trustArchitecture';
import api from '../services/api';

interface LandingProps {
  isDebug?: boolean;
}

/** Content overrides fetched from the site-pages API (page builder). */
interface SitePageOverrides {
  heroHeading?: string;
  heroSubheading?: string;
  heroBody?: string;
  heroCta?: string;
  heroCtaUrl?: string;
  heroSecondaryCta?: string;
  heroSecondaryCtaUrl?: string;
  heroBackgroundImage?: string;
  howItWorksHeading?: string;
  howItWorksSteps?: Array<{ title: string; description: string }>;
  hiveFundHeading?: string;
  hiveFundTargetIrr?: string;
  hiveFundTargetCashflow?: string;
  hiveFundMinimum?: string;
  hiveFundReserves?: string;
  hiveFundImage?: string;
  hiveFundCta?: string;
  hiveFundSecondaryCta?: string;
  faqHeading?: string;
  faqItems?: Array<{ question: string; answer: string }>;
  newsletterHeading?: string;
  newsletterSubheading?: string;
  footerDisclaimer?: string;
  // REDEX-specific
  platformHeading?: string;
  platformSubheading?: string;
  platformPillars?: Array<{ title: string; description: string }>;
  builtForTags?: string[];
  railsHeading?: string;
  railsBody?: string;
  railsCards?: Array<{ title: string; description: string }>;
  invoicesHeading?: string;
  invoicesSubheading?: string;
  invoicesCards?: Array<{ title: string; description: string }>;
}

/**
 * Parse site page section_content into flat overrides that Landing.tsx uses.
 * Supports two formats:
 * 1. Block-based: content.blocks[] with props (legacy page builder format)
 * 2. Section-based: content.settings.sections.{sectionId}.{field} (CMS editor format)
 */
function parseSitePageContent(content: any): SitePageOverrides {
  const overrides: SitePageOverrides = {};

  // ── Format 2: Section-based (CMS editor) ──
  const sections = content?.settings?.sections;
  if (sections) {
    const h = sections.hero || {};
    if (h.heading) overrides.heroHeading = h.heading;
    if (h.subheading) overrides.heroSubheading = h.subheading;
    if (h.body) overrides.heroBody = h.body;
    if (h.ctaText) overrides.heroCta = h.ctaText;
    if (h.ctaUrl) overrides.heroCtaUrl = h.ctaUrl;
    if (h.secondaryCtaText) overrides.heroSecondaryCta = h.secondaryCtaText;
    if (h.secondaryCtaUrl) overrides.heroSecondaryCtaUrl = h.secondaryCtaUrl;
    if (h.backgroundImage) overrides.heroBackgroundImage = h.backgroundImage;

    const hw = sections.howItWorks || {};
    if (hw.heading) overrides.howItWorksHeading = hw.heading;
    if (Array.isArray(hw.steps) && hw.steps.length) {
      overrides.howItWorksSteps = hw.steps;
    } else if (hw.step1Title || hw.step2Title || hw.step3Title) {
      overrides.howItWorksSteps = [
        { title: hw.step1Title || 'Browse', description: hw.step1Desc || '' },
        { title: hw.step2Title || 'Invest', description: hw.step2Desc || '' },
        { title: hw.step3Title || 'Earn', description: hw.step3Desc || '' },
      ];
    }

    const hf = sections.hiveFund || {};
    if (hf.heading) overrides.hiveFundHeading = hf.heading;
    if (hf.targetIrr) overrides.hiveFundTargetIrr = hf.targetIrr;
    if (hf.targetCashflow) overrides.hiveFundTargetCashflow = hf.targetCashflow;
    if (hf.minimumInvestment) overrides.hiveFundMinimum = hf.minimumInvestment;
    if (hf.reserves) overrides.hiveFundReserves = hf.reserves;
    if (hf.image) overrides.hiveFundImage = hf.image;
    if (hf.ctaText) overrides.hiveFundCta = hf.ctaText;
    if (hf.secondaryCtaText) overrides.hiveFundSecondaryCta = hf.secondaryCtaText;

    const faq = sections.faq || {};
    if (faq.heading) overrides.faqHeading = faq.heading;
    if (faq.items?.length) overrides.faqItems = faq.items;

    const nl = sections.newsletter || {};
    if (nl.heading) overrides.newsletterHeading = nl.heading;
    if (nl.subheading) overrides.newsletterSubheading = nl.subheading;

    const ft = sections.footer || {};
    if (ft.disclaimer) overrides.footerDisclaimer = ft.disclaimer;

    // REDEX sections
    const pl = sections.platform || {};
    if (pl.heading) overrides.platformHeading = pl.heading;
    if (pl.subheading) overrides.platformSubheading = pl.subheading;
    if (Array.isArray(pl.pillars) && pl.pillars.length) {
      overrides.platformPillars = pl.pillars;
    } else if (pl.pillar1Title || pl.pillar2Title || pl.pillar3Title || pl.pillar4Title) {
      overrides.platformPillars = [1, 2, 3, 4]
        .filter(n => pl[`pillar${n}Title`])
        .map(n => ({ title: pl[`pillar${n}Title`], description: pl[`pillar${n}Desc`] || '' }));
    }
    if (pl.builtForTags) {
      const raw = typeof pl.builtForTags === 'string'
        ? pl.builtForTags.split(',').map((s: string) => s.trim()).filter(Boolean)
        : Array.isArray(pl.builtForTags) ? pl.builtForTags : [];
      if (raw.length) overrides.builtForTags = raw;
    }

    const rl = sections.rails || {};
    if (rl.heading) overrides.railsHeading = rl.heading;
    if (rl.body) overrides.railsBody = rl.body;
    if (Array.isArray(rl.cards) && rl.cards.length) {
      overrides.railsCards = rl.cards;
    } else if (rl.card1Title || rl.card2Title || rl.card3Title || rl.card4Title) {
      overrides.railsCards = [1, 2, 3, 4]
        .filter(n => rl[`card${n}Title`])
        .map(n => ({ title: rl[`card${n}Title`], description: rl[`card${n}Desc`] || '' }));
    }

    const inv = sections.invoices || {};
    if (inv.heading) overrides.invoicesHeading = inv.heading;
    if (inv.subheading) overrides.invoicesSubheading = inv.subheading;
    if (Array.isArray(inv.cards) && inv.cards.length) {
      overrides.invoicesCards = inv.cards;
    } else if (inv.card1Title || inv.card2Title) {
      overrides.invoicesCards = [1, 2]
        .filter(n => inv[`card${n}Title`])
        .map(n => ({ title: inv[`card${n}Title`], description: inv[`card${n}Desc`] || '' }));
    }

    return overrides;
  }

  // ── Format 1: Block-based (legacy) ──
  if (!content?.blocks) return overrides;

  for (const block of content.blocks) {
    if (!block.visible) continue;
    const p = block.props || {};
    const key = block.id || '';

    // Hero
    if (key.includes('hero') && block.blockKey === 'hero') {
      if (p.heading) overrides.heroHeading = p.heading;
      if (p.subheading) overrides.heroSubheading = p.subheading;
      if (p.body) overrides.heroBody = p.body;
      if (p.ctaText) overrides.heroCta = p.ctaText;
      if (p.ctaUrl) overrides.heroCtaUrl = p.ctaUrl;
      if (p.secondaryCtaText) overrides.heroSecondaryCta = p.secondaryCtaText;
      if (p.secondaryCtaUrl) overrides.heroSecondaryCtaUrl = p.secondaryCtaUrl;
    }

    // How it works (Honeyshares content block)
    if (key.includes('how-it-works') || key.includes('howItWorks')) {
      if (p.heading) overrides.howItWorksHeading = p.heading;
      if (p.steps) overrides.howItWorksSteps = p.steps;
    }

    // Hive Fund
    if (key.includes('hive-fund') || key.includes('hiveFund')) {
      if (p.heading) overrides.hiveFundHeading = p.heading;
      if (p.targetIrr) overrides.hiveFundTargetIrr = p.targetIrr;
      if (p.targetCashflow) overrides.hiveFundTargetCashflow = p.targetCashflow;
      if (p.minimum) overrides.hiveFundMinimum = p.minimum;
      if (p.reserves) overrides.hiveFundReserves = p.reserves;
    }

    // FAQ
    if (key.includes('faq') && block.blockKey === 'faq') {
      if (p.heading) overrides.faqHeading = p.heading;
      if (p.items) overrides.faqItems = p.items;
    }

    // Newsletter
    if (key.includes('newsletter')) {
      if (p.heading) overrides.newsletterHeading = p.heading;
      if (p.body) overrides.newsletterSubheading = p.body;
    }

    // Footer
    if (key.includes('footer')) {
      if (p.disclaimer) overrides.footerDisclaimer = p.disclaimer;
    }

    // REDEX: Platform section
    if (key.includes('platform') && block.blockKey === 'content') {
      if (p.heading) overrides.platformHeading = p.heading;
      if (p.subheading || p.body) overrides.platformSubheading = p.subheading || p.body;
      if (p.pillars) overrides.platformPillars = p.pillars;
      if (p.builtForTags) overrides.builtForTags = p.builtForTags;
    }

    // REDEX: Rails
    if (key.includes('rails') && block.blockKey !== 'footer' && block.blockKey !== 'navbar') {
      if (p.heading) overrides.railsHeading = p.heading;
      if (p.body) overrides.railsBody = p.body;
      if (p.cards) overrides.railsCards = p.cards;
    }

    // REDEX: Invoices
    if (key.includes('invoic')) {
      if (p.heading) overrides.invoicesHeading = p.heading;
      if (p.subheading || p.body) overrides.invoicesSubheading = p.subheading || p.body;
      if (p.cards) overrides.invoicesCards = p.cards;
    }
  }

  return overrides;
}

const Landing: React.FC<LandingProps> = ({ isDebug = false }) => {
  const [overrides, setOverrides] = useState<SitePageOverrides>({});
  const [dynamicNav, setDynamicNav] = useState<NavItem[] | null>(null);

  useEffect(() => {
    api.get('/site-pages/home')
      .then((res: any) => {
        const data = res?.data?.data || res?.data || res;
        if (data?.content) {
          setOverrides(parseSitePageContent(data.content));
        }
      })
      .catch(() => { /* no published home page — use hardcoded defaults */ });

    // Fetch dynamic navigation from the site page builder
    api.get('/site-pages/nav')
      .then((res: any) => {
        const items = res?.data?.data || res?.data || [];
        if (Array.isArray(items) && items.length > 0) {
          setDynamicNav(items.map((item: any) => ({
            label: item.label,
            ...(item.scrollTo ? { scrollTo: item.scrollTo } : {}),
            ...(item.path ? { path: item.path } : {}),
          })));
        }
      })
      .catch(() => { /* fall back to hardcoded nav */ });
  }, []);
  const [emailInput, setEmailInput] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [newsletterError, setNewsletterError] = useState('');
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterError('');

    if (!emailInput) {
      setNewsletterError('Please enter your email');
      return;
    }

    if (!emailInput.includes('@') || !emailInput.includes('.')) {
      setNewsletterError('Please enter a valid email address');
      return;
    }

    setIsSubscribing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setNewsletterSuccess(true);
      setEmailInput('');
    } catch (error) {
      setNewsletterError('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveSection(id);
  };

  const isRedex = TENANT.id === 'redex';

  // Hardcoded fallbacks per tenant
  const fallbackNav: NavItem[] = isRedex
    ? [
        { label: 'Home', scrollTo: 'home' },
        { label: 'Platform', scrollTo: 'platform' },
        { label: 'Trust Architecture', scrollTo: 'trust' },
        { label: 'Rails', scrollTo: 'rails' },
        { label: 'Invoices', scrollTo: 'invoices' },
        { label: 'Contact', scrollTo: 'contact' },
      ]
    : [
        { label: 'Home', scrollTo: 'home' },
        { label: 'Invest', path: '/invest' },
        { label: 'Hive Fund', path: '/hive-fund' },
        { label: 'About', path: '/about' },
        { label: 'Contact', scrollTo: 'contact' },
      ];

  const navItems = dynamicNav || fallbackNav;

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader
        navItems={navItems}
        activeSection={activeSection}
        onScrollToSection={scrollToSection}
      />

      {isRedex ? (
        <React.Fragment>
          {/* Hero Section */}
          <section
            id="home"
            className="
              min-h-[75vh]
              md:min-h-[80vh]
              flex items-center
              bg-[hsl(var(--dark-bg))]
              text-[hsl(var(--dark-foreground))]
            "
            style={overrides.heroBackgroundImage ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.5)), url(${overrides.heroBackgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center', imageRendering: 'auto' } : undefined}
          >
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                  <div className="lg:col-span-7">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium mb-6 leading-tight">
                      {overrides.heroHeading ? (
                        <>{overrides.heroHeading}</>
                      ) : (
                        <>American Dynamism{' '}<span className="text-primary">Unleashed</span></>
                      )}
                    </h1>

                    <p className="text-lg sm:text-xl md:text-2xl text-gray-400 mb-6 max-w-3xl leading-relaxed">
                      {overrides.heroSubheading || 'Capital infrastructure for builders, operators, and owners shaping the next generation of industry.'}
                    </p>

                    <p className="text-base sm:text-lg text-gray-400 mb-8 max-w-3xl leading-relaxed">
                      {overrides.heroBody || 'We believe ownership is the foundation of sovereignty, opportunity, and long-term prosperity. REDEX builds programmable capital rails that allow sovereign operators to structure, raise, and manage capital under their own brand — without marketplace extraction or platform dependency.'}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/register">
                        <Button size="lg" className="text-base font-semibold px-8 w-full sm:w-auto">
                          REQUEST ACCESS
                        </Button>
                      </Link>
                      <Link to="/demo">
                        <button
                          type="button"
                          className="text-base font-semibold px-8 py-3 rounded-md w-full sm:w-auto border border-white/20 text-white hover:bg-white/10 transition-colors"
                        >
                          SEE THE PLATFORM
                        </button>
                      </Link>
                    </div>
                  </div>

                  <div className="lg:col-span-5">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
                      <div className="flex items-center gap-3 mb-6">
                        <img src={BRAND_ASSETS.logo.primary} alt={TENANT.branding.name} className="h-10 w-auto" />
                        <div>
                          <p className="text-sm text-gray-400">Capital infrastructure</p>
                          <p className="text-lg font-semibold">{TENANT.branding.name}</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                          <div>
                            <p className="font-semibold">Sovereignty</p>
                            <p className="text-sm text-gray-400">Operators control the capital stack — no platform dependency.</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                          <div>
                            <p className="font-semibold">Ownership-first</p>
                            <p className="text-sm text-gray-400">Equity and structured ownership — not speculation.</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                          <div>
                            <p className="font-semibold">Industrial execution</p>
                            <p className="text-sm text-gray-400">Hard assets, real businesses, and real revenue workflows.</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                          <div>
                            <p className="font-semibold">Capital velocity</p>
                            <p className="text-sm text-gray-400">Bank-grade rails, KYC gates, and auditable lifecycle tracking.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Platform - WHITE */}
          <section id="platform" className="py-16 md:py-24 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12 md:mb-16">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-foreground">{overrides.platformHeading || 'Capital sovereignty for builders'}</h2>
                  <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                    {overrides.platformSubheading || 'REDEX exists to restore capital sovereignty to builders — infrastructure that lets operators raise and manage capital under their own brand.'}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {(overrides.platformPillars || [
                    { title: 'Sovereignty', description: 'Operators control the capital stack. No marketplace extraction. No platform dependency.' },
                    { title: 'Ownership', description: 'Everything centers around equity and structured ownership — not token hype.' },
                    { title: 'Industrialization', description: 'Hard assets, real businesses, real infrastructure — built for operators.' },
                    { title: 'Opportunity', description: 'Capital formation for builders and operators — aligned with real economy workflows.' },
                  ]).map((pillar, idx) => (
                    <div key={idx} className="rounded-2xl border border-border bg-card p-6">
                      <h3 className="text-xl font-bold mb-2 text-foreground">{pillar.title}</h3>
                      <p className="text-muted-foreground">{pillar.description}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-10 rounded-2xl border border-border bg-muted p-6">
                  <h3 className="text-lg font-bold text-foreground mb-3">Built for</h3>
                  <div className="flex flex-wrap gap-2">
                    {(overrides.builtForTags || [
                      'Commercial operators',
                      'Acquisition vehicles',
                      'Private funds',
                      'Hard assets',
                      'R&D capital formation',
                      'Structured ownership',
                    ]).map((label) => (
                      <span
                        key={label}
                        className="inline-flex items-center rounded-full bg-background border border-border px-3 py-1 text-sm text-muted-foreground"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Trust Architecture - DARK */}
          <section id="trust" className="py-16 md:py-24 bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))]">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <div className="mb-12 md:mb-16">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Trust Architecture</h2>
                  <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">{TRUST_INTRO}</p>
                </div>

                <div className="space-y-10">
                  {TRUST_SECTIONS.map((section) => {
                    const Icon = section.icon;
                    return (
                      <div key={section.id} className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="mt-1 shrink-0">
                            <Icon className="h-6 w-6 text-gray-300" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold mb-2">{section.title}</h3>
                            <p className="text-sm text-gray-400 leading-relaxed">{section.summary}</p>
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3 mb-4 ml-10">
                          {section.controls.map((ctrl) => (
                            <div key={ctrl.control} className="bg-white/5 border border-white/5 rounded-lg px-4 py-3">
                              <p className="text-sm font-semibold">{ctrl.control}</p>
                              <p className="text-xs text-gray-500 mt-1">{ctrl.purpose}</p>
                            </div>
                          ))}
                        </div>

                        <p className="text-sm text-gray-300 italic ml-10">{section.keyStatement}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-12 bg-white/5 border border-white/10 rounded-xl p-6 md:p-8 flex items-start gap-4">
                  {(() => { const Fwd = FORWARD_ICON; return <Fwd className="h-6 w-6 text-gray-300 mt-1 shrink-0" />; })()}
                  <div>
                    <h3 className="text-lg font-bold mb-2">Forward Look</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{TRUST_FORWARD_STATEMENT}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Rails - WHITE */}
          <section id="rails" className="py-16 md:py-24 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <div className="grid md:grid-cols-2 gap-10 items-start">
                  <div>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-foreground">{overrides.railsHeading || 'Programmable capital rails'}</h2>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {overrides.railsBody || 'Infrastructure-first banking alignment: KYC gates, domestic settlement, and modern ownership workflows — delivered under your brand.'}
                    </p>
                  </div>
                  <div className="space-y-4">
                    {(overrides.railsCards || [
                      { title: 'White-labeled portals', description: 'A fully white-labeled investor portal for operators raising under their own brand.' },
                      { title: 'Bank-grade ACH rails', description: 'Plaid + ACH for payables, distributions, and treasury movement.' },
                      { title: 'KYC-gated onboarding', description: 'Compliance gates that protect offerings and counterparties — without user friction.' },
                      { title: 'Crypto-enabled settlement (optional)', description: 'Use on-chain settlement where it fits — designed for execution, not speculation.' },
                    ]).map((card, idx) => (
                      <div key={idx} className="rounded-2xl border border-border bg-card p-5">
                        <p className="font-semibold text-foreground">{card.title}</p>
                        <p className="text-sm text-muted-foreground">{card.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Invoices - DARK */}
          <section id="invoices" className="py-16 md:py-24 bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))]">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{overrides.invoicesHeading || 'Transaction + invoicing infrastructure'}</h2>
                  <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                    {overrides.invoicesSubheading || 'API-connected invoicing and auditable execution flows — designed to integrate with real operators and real balance sheets.'}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {(overrides.invoicesCards || [
                    { title: 'Structured invoice workflows', description: 'Represent obligations as trackable objects with controlled access and lifecycle state.' },
                    { title: 'Approvals, audit, and reporting', description: 'Capture approvals, timestamps, and settlement events end-to-end for clean reporting.' },
                  ]).map((card, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6">
                      <h3 className="text-xl font-bold mb-2">{card.title}</h3>
                      <p className="text-gray-400">{card.description}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-10 flex justify-center">
                  <Link to="/register">
                    <Button size="lg" className="font-semibold px-10">GET STARTED</Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section - WHITE */}
          <section id="contact" className="py-16 md:py-24 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <JoinTheHiveForm embedded />
              </div>
            </div>
          </section>

          {/* Footer - DARK */}
          <footer className="bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))] border-t border-white/10 py-12 md:py-16">
            <div className="container mx-auto px-4">
              <div className="border-t border-white/10 pt-8">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-2">
                    <img
                      src={BRAND_ASSETS.logo.allWhite}
                      alt={TENANT.branding.name}
                      className="h-6 w-auto"
                    />
                    <span className="text-sm text-gray-400">© 2026 {TENANT.branding.name}. All rights reserved.</span>
                  </div>
                  <div className="flex gap-6 text-sm text-gray-400">
                    <Link to="/terms" className="hover:text-primary transition-colors">
                      Terms of Service
                    </Link>
                    <Link to="/privacy" className="hover:text-primary transition-colors">
                      Privacy Policy
                    </Link>
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center mt-6 max-w-4xl mx-auto">
                  Platform usage involves risk, including loss of funds and operational errors. Past performance does not guarantee future results.
                </p>
              </div>
            </div>
          </footer>
        </React.Fragment>
      ) : (
        <React.Fragment>

      {/* Hero Section */}
      <section
        id="home"
        className="
          min-h-[75vh]
          md:min-h-[80vh]
          flex items-center
          bg-[hsl(var(--dark-bg))]
          text-[hsl(var(--dark-foreground))]
        "
        style={overrides.heroBackgroundImage ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.5)), url(${overrides.heroBackgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center', imageRendering: 'auto' as any } : undefined}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium mb-6 md:mb-8 leading-tight">
              {overrides.heroHeading ? (
                <>{overrides.heroHeading}</>
              ) : (
                <>Real Estate Investing,{' '}<span className="text-primary">Simplified</span></>
              )}
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-gray-400 mb-8 md:mb-12 max-w-4xl mx-auto leading-relaxed">
              {overrides.heroSubheading || 'Institutional-grade real estate, simplified for the modern investor'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="text-base font-semibold px-8 w-full sm:w-auto">
                  Get Started
                </Button>
              </Link>
              {isDebug && (
                <Link to="/demo">
                  <Button
                    size="lg"
                    className="text-base font-semibold px-8 w-full sm:w-auto bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    View Demo
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - WHITE */}
      <section id="about" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-foreground">{overrides.howItWorksHeading || 'How It Works'}</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-12 md:gap-16 max-w-5xl mx-auto">
            {(overrides.howItWorksSteps || [
              { title: 'Browse', description: 'Explore our curated selection of institutional-grade real estate investments' },
              { title: 'Invest', description: 'Choose properties that align with your investment goals and risk tolerance' },
              { title: 'Earn', description: 'Receive regular distributions and benefit from property appreciation' },
            ]).map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl md:text-4xl font-bold mx-auto mb-6">
                  {idx + 1}
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-3 text-foreground">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Hive Fund - DARK */}
      <section id="invest" className="py-16 md:py-24 bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left: Image */}
              <div className="order-2 md:order-1">
                <img
                  src={overrides.hiveFundImage || "/HiveFund.jpg"}
                  alt="The Hive Fund"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>

              {/* Right: Content */}
              <div className="order-1 md:order-2">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                  The Hive Fund
                </h2>
                <div className="space-y-4 mb-8">
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl md:text-5xl font-medium text-primary">18%+</span>
                    <span className="text-lg text-gray-400">target IRR</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl md:text-5xl font-medium text-primary">12%+</span>
                    <span className="text-lg text-gray-400">target annual cashflow</span>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-400">
                    <span className="font-semibold text-white">Accredited investors only.</span> Minimum investment $10,000
                  </p>
                </div>
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-8">
                  <p className="text-sm font-medium text-white">
                    6 months of reserves
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/register" className="flex-1">
                    <Button size="lg" className="w-full font-semibold">
                      Request Access
                    </Button>
                  </Link>
                  <Link to="/hive-fund" className="flex-1">
                    <Button size="lg" variant="outline" className="w-full font-semibold">
                      Learn More
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - WHITE */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 md:mb-12 text-center text-foreground">
              {overrides.faqHeading || 'Frequently Asked Questions'}
            </h2>
            <SimpleAccordion
              items={[
                {
                  value: 'item-1',
                  trigger: 'How does investing with Honeyshares work?',
                  content: 'Honeyshares gives eligible investors access to curated property-backed opportunities with professional management, investor reporting, and a streamlined subscription experience.',
                  className: 'bg-white border-border',
                },
                {
                  value: 'item-2',
                  trigger: 'How do I earn returns?',
                  content: 'You earn returns in two ways: (1) Regular dividend payments from rental income generated by the properties, and (2) Capital appreciation when property values increase over time. Returns are automatically deposited to your account.',
                  className: 'bg-white border-border',
                },
                {
                  value: 'item-3',
                  trigger: 'Is my investment secure?',
                  content: 'We take security seriously and use industry-standard safeguards across accounts and data flows. Each opportunity is reviewed before listing. All investments involve risk, including possible loss of principal.',
                  className: 'bg-white border-border',
                },
                {
                  value: 'item-4',
                  trigger: 'What is The Hive Fund?',
                  content: 'The Hive Fund is our flagship diversified real estate fund, offering institutional-grade returns with professional management. It targets 18%+ IRR and 12%+ annual cashflow, with 6 months of reserves for added security.',
                  className: 'bg-white border-border',
                },
                {
                  value: 'item-5',
                  trigger: 'Who can invest?',
                  content: 'The Hive Fund is available to accredited investors with a minimum investment of $10,000. Individual property investments may have different requirements and lower minimums.',
                  className: 'bg-white border-border',
                },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Newsletter Section - DARK */}
      <section id="newsletter" className="py-16 md:py-24 bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))]">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">{overrides.newsletterHeading || 'Stay Updated'}</h2>
            <p className="text-lg text-gray-400 mb-8">
              {overrides.newsletterSubheading || 'Get the latest investment opportunities and market insights delivered to your inbox'}
            </p>
            {newsletterSuccess ? (
              <div className="max-w-lg mx-auto bg-primary/10 border border-primary/20 rounded-lg p-6">
                <div className="flex items-center justify-center gap-3">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-lg font-medium">Thanks for subscribing! Check your email.</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubscribe} className="max-w-lg mx-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      if (newsletterError) setNewsletterError('');
                    }}
                    placeholder="Enter your email"
                    className={`flex-1 px-4 py-3 rounded-md border bg-white/5 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 transition-all ${
                      newsletterError
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-white/10 focus:ring-primary'
                    }`}
                    disabled={isSubscribing}
                  />
                  <Button type="submit" size="lg" disabled={isSubscribing} className="font-semibold">
                    {isSubscribing ? 'Subscribing...' : 'SUBSCRIBE'}
                  </Button>
                </div>
                {newsletterError && (
                  <p className="mt-2 text-sm text-red-400 text-left">{newsletterError}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section - WHITE */}
      <section id="contact" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <JoinTheHiveForm embedded />
          </div>
        </div>
      </section>

      {/* Footer - DARK */}
      <footer className="bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))] border-t border-white/10 py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-2 gap-8 md:gap-12 mb-8">
            {/* Invest */}
            <div>
              <h4 className="font-bold text-base mb-4">Invest</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <Link to="/hive-fund" className="hover:text-primary transition-colors">
                    The Hive Fund
                  </Link>
                </li>
                <li>
                  <Link to="/invest" className="hover:text-primary transition-colors">
                    Browse Properties
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="hover:text-primary transition-colors">
                    Get Started
                  </Link>
                </li>
              </ul>
            </div>


            {/*  TODO: RESTORE THESE WHEN LINKS BECOME AVAILABLE - mp 
            Learn
            <div>
              <h4 className="font-bold text-base mb-4">Learn</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <button onClick={() => scrollToSection('about')} className="hover:text-primary transition-colors">
                    How It Works
                  </button>
                </li>
                <li>
                  <button onClick={() => window.location.href = '#'} className="hover:text-primary transition-colors">
                    FAQ
                  </button>
                </li>
                <li>
                  <button onClick={() => window.location.href = '#'} className="hover:text-primary transition-colors">
                    Resources
                  </button>
                </li>
              </ul>
            </div> */}

            {/* Company
            <div>
              <h4 className="font-bold text-base mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <button onClick={() => window.location.href = '#'} className="hover:text-primary transition-colors">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => window.location.href = '#'} className="hover:text-primary transition-colors">
                    Contact
                  </button>
                </li>
                <li>
                  <button onClick={() => window.location.href = '#'} className="hover:text-primary transition-colors">
                    Partner With Us
                  </button>
                </li>
              </ul>
            </div> */}

            {/* Follow Us */}
            <div>
              <h4 className="font-bold text-base mb-4">Follow Us</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="https://x.com/honeyshares" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    X (Twitter)
                  </a>
                </li>
                <li>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    Instagram
                  </a>
                </li>
                <li>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    Facebook
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2">
                <img
                  src={TENANT.id === 'redex' ? BRAND_ASSETS.logo.primary : BRAND_ASSETS.logo.allWhite}
                  alt={TENANT.branding.name}
                  className="h-6 w-auto"
                />
                <span className="text-sm text-gray-400">© 2026 {TENANT.branding.name}. All rights reserved.</span>
              </div>
              <div className="flex gap-6 text-sm text-gray-400">
                <Link to="/terms" className="hover:text-primary transition-colors">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="hover:text-primary transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-6 max-w-4xl mx-auto">
              Investing in real estate involves risk, including loss of principal. Past performance does not guarantee future results.
              Securities offered through registered broker-dealer. This is not an offer to sell securities.
            </p>
          </div>
        </div>
      </footer>
        </React.Fragment>
      )}
    </div>
  );
};

export default Landing;
